import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SEO } from "../components/SEO";
import { contactInfo } from "../data/mock";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  MapPin, Phone, Mail, Clock, Send,
  Facebook, Instagram, Linkedin, Twitter
} from "lucide-react";

export default function ContactPage() {
  const [services, setServices] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    api.getServices().then(setServices).catch(console.error);
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setIsSubmitting(true);
    try {
      await api.submitContact(formData);
      // Send lead details to WhatsApp
      const whatsappMsg = `New Consultation Lead:%0A*Name:* ${formData.name}%0A*Email:* ${formData.email}%0A*Phone:* ${formData.phone || 'Not provided'}%0A*Service:* ${formData.service || 'General'}%0A*Message:* ${formData.message}`;
      window.open(`https://wa.me/918248323674?text=${whatsappMsg}`, '_blank');
      toast.success("Message sent successfully!", {
        description: "We'll get back to you within 24 hours."
      });
      setFormData({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Failed to send message. Please try again.");
    }
    setIsSubmitting(false);
  };

  return (
    <div>
      <SEO 
        title="Contact Us - Book Free Consultation"
        description="Get in touch with NexFLY Immigration Consultancy. Book your free consultation today. Located in Coimbatore, we help clients across India achieve their immigration goals."
        keywords="contact NexFLY, immigration consultation, visa consultancy Coimbatore, book consultation, immigration expert contact"
      />
      {/* Hero */}
      <section className="relative py-20 bg-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[45%] h-full bg-brand-50/50 rounded-bl-[100px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <Badge className="bg-brand-100 text-brand-700 hover:bg-brand-100 mb-6 px-4 py-1.5 text-xs font-semibold rounded-full">
              Get In Touch
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight mb-5">
              Let's Start Your
              <span className="text-brand-500 block">Journey Together</span>
            </h1>
            <p className="text-lg text-gray-500 leading-relaxed">
              Have questions? Ready to begin? Our immigration experts are here to guide you every step of the way.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-10">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <Card className="border-0 shadow-sm bg-white rounded-2xl">
                <CardContent className="p-8 sm:p-10">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Send Us a Message</h2>
                  <p className="text-gray-500 text-sm mb-8">Fill out the form below and we'll get back to you within 24 hours.</p>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-sm font-medium text-gray-700">Full Name *</Label>
                        <Input
                          id="name"
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={(e) => handleChange("name", e.target.value)}
                          className="rounded-xl h-11 border-gray-200 focus:border-brand-300 focus:ring-brand-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email Address *</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="john@example.com"
                          value={formData.email}
                          onChange={(e) => handleChange("email", e.target.value)}
                          className="rounded-xl h-11 border-gray-200 focus:border-brand-300 focus:ring-brand-200"
                        />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-sm font-medium text-gray-700">Phone Number</Label>
                        <Input
                          id="phone"
                          placeholder="+91 98765 43210"
                          value={formData.phone}
                          onChange={(e) => handleChange("phone", e.target.value)}
                          className="rounded-xl h-11 border-gray-200 focus:border-brand-300 focus:ring-brand-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700">Service Interested In</Label>
                        <Select value={formData.service} onValueChange={(val) => handleChange("service", val)}>
                          <SelectTrigger className="rounded-xl h-11 border-gray-200 focus:border-brand-300 focus:ring-brand-200">
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent className="rounded-xl">
                            {services.map((s) => (
                              <SelectItem key={s.slug} value={s.slug} className="rounded-lg">
                                {s.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-sm font-medium text-gray-700">Your Message *</Label>
                      <Textarea
                        id="message"
                        placeholder="Tell us about your immigration goals..."
                        rows={5}
                        value={formData.message}
                        onChange={(e) => handleChange("message", e.target.value)}
                        className="rounded-xl border-gray-200 focus:border-brand-300 focus:ring-brand-200 resize-none"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-brand-500 hover:bg-brand-700 text-white rounded-xl h-12 px-8 text-sm font-semibold shadow-lg shadow-brand-200/60 hover:shadow-brand-300/60 transition-all duration-300 hover:-translate-y-0.5 w-full sm:w-auto"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sending...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Send className="w-4 h-4" /> Send Message
                        </span>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="border-0 shadow-sm bg-white rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="text-lg font-bold text-gray-900 mb-6">Contact Information</h3>
                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-brand-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-brand-500" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 mb-0.5">Visit Us</p>
                        <p className="text-sm text-gray-500 leading-relaxed">{contactInfo.address}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-brand-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-brand-500" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 mb-0.5">Call Us</p>
                        <a href={`tel:${contactInfo.phone}`} className="text-sm text-brand-500 hover:text-brand-700 transition-colors">{contactInfo.phone}</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-brand-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-brand-500" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 mb-0.5">Email Us</p>
                        <a href={`mailto:${contactInfo.email}`} className="text-sm text-brand-500 hover:text-brand-700 transition-colors">{contactInfo.email}</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-brand-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-brand-500" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 mb-0.5">Business Hours</p>
                        <p className="text-sm text-gray-500">{contactInfo.hours}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm bg-white rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Follow Us</h3>
                  <p className="text-sm text-gray-500 mb-5">Stay connected on social media for updates and tips.</p>
                  <div className="flex items-center gap-3">
                    <a href={contactInfo.social.facebook} target="_blank" rel="noreferrer" className="w-10 h-10 bg-brand-100 rounded-xl flex items-center justify-center hover:bg-brand-500 hover:text-white text-brand-500 transition-all duration-200">
                      <Facebook className="w-5 h-5" />
                    </a>
                    <a href={contactInfo.social.instagram} target="_blank" rel="noreferrer" className="w-10 h-10 bg-brand-100 rounded-xl flex items-center justify-center hover:bg-brand-500 hover:text-white text-brand-500 transition-all duration-200">
                      <Instagram className="w-5 h-5" />
                    </a>
                    <a href={contactInfo.social.linkedin} target="_blank" rel="noreferrer" className="w-10 h-10 bg-brand-100 rounded-xl flex items-center justify-center hover:bg-brand-500 hover:text-white text-brand-500 transition-all duration-200">
                      <Linkedin className="w-5 h-5" />
                    </a>
                    <a href={contactInfo.social.twitter} target="_blank" rel="noreferrer" className="w-10 h-10 bg-brand-100 rounded-xl flex items-center justify-center hover:bg-brand-500 hover:text-white text-brand-500 transition-all duration-200">
                      <Twitter className="w-5 h-5" />
                    </a>
                  </div>
                </CardContent>
              </Card>

              {/* Map Placeholder */}
              <Card className="border-0 shadow-sm bg-white rounded-2xl overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3916.2!2d76.97!3d11.02!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba859af2f461b59%3A0x0!2sPappanaickenpalayam%2C%20Coimbatore%2C%20Tamil%20Nadu%20641037!5e0!3m2!1sen!2sin!4v1700000000000"
                  width="100%"
                  height="220"
                  style={{border: 0}}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="NexFLY Immigration Office Location"
                  className="rounded-2xl"
                />
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
